import {
  type ActionRejected,
  type ChatMsg,
  CONFIG,
  type EmoteBroadcast,
  GAME_ERROR,
  type LeaderboardMessage,
  type PlayerEvent,
  type PositionCorrection,
  type ProjectStartedMessage,
  type Results,
  type WelcomeMessage,
} from "@agency/shared";
import { Client, type Room as SDKRoom } from "@colyseus/sdk";
import { CLIENT_VERSION, resolveServerUrl } from "../config.js";
import { store } from "../store/GameStore.js";
import { bindState } from "./stateBindings.js";

export interface JoinInput {
  token: string;
  nickname: string;
  avatarId: string;
  role: string;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Owns the Colyseus connection: join, reconnect, leave and typed sends. */
export class NetClient {
  private readonly client = new Client(resolveServerUrl());
  room?: SDKRoom;
  private leaving = false;

  async join(input: JoinInput): Promise<SDKRoom> {
    store.emit("connection", { state: "connecting" });
    const room = await this.client.join("office", { ...input, clientVersion: CLIENT_VERSION });
    this.attach(room);
    return room;
  }

  private attach(room: SDKRoom): void {
    this.room = room;
    this.leaving = false;
    // Reconnection is driven by handleLeave (overlay + state rebind), so the
    // SDK's built-in retry must not swallow drops before onLeave fires.
    room.reconnection.enabled = false;
    // Install before waiting for state, so a drop during the handshake still
    // retries or closes instead of hanging.
    room.onLeave((code) => void this.handleLeave(room, code));

    const ready = () => {
      bindState(room);
      this.registerHandlers(room);
      store.emit("connection", { state: "joined" });
      // Ask for the welcome payload once our handlers are attached, so chat
      // history and server time are never lost to slow handler registration.
      room.send("hello", {});
    };

    const state = room.state as { project?: unknown } | undefined;
    if (state?.project) ready();
    else room.onStateChange.once(() => ready());
  }

  private registerHandlers(room: SDKRoom): void {
    const on = room.onMessage.bind(room) as unknown as (
      type: string,
      cb: (payload: unknown) => void,
    ) => void;
    on("welcome", (m) => {
      const welcome = m as WelcomeMessage;
      store.serverOffset = welcome.serverTime - Date.now();
      store.timeScale = welcome.timeScale;
      store.lastWelcome = welcome;
      store.emit("welcome", welcome);
    });
    on("chat", (m) => store.emit("chat", m as ChatMsg));
    on("emote", (m) => store.emit("emote", m as EmoteBroadcast));
    on("positionCorrection", (m) => store.emit("correction", m as PositionCorrection));
    on("actionRejected", (m) => store.emit("rejected", m as ActionRejected));
    on("playerEvent", (m) => store.emit("playerEvent", m as PlayerEvent));
    on("projectCompleted", (m) => store.emit("results", m as Results));
    on("projectStarted", (m) => store.emit("projectStarted", m as ProjectStartedMessage));
    on("leaderboard", (m) => store.emit("leaderboard", m as LeaderboardMessage));
  }

  private async handleLeave(room: SDKRoom, code: number): Promise<void> {
    // 4000 = consented exit; a replaced session has no seat to reconnect to.
    if (this.leaving || code === 4000 || code === GAME_ERROR.DUPLICATE_SESSION) {
      store.emit("connection", { state: "closed", code });
      return;
    }

    store.emit("connection", { state: "reconnecting" });
    const deadline = Date.now() + CONFIG.RECONNECT_GRACE_S * 1000;
    let delay = 500;

    while (Date.now() < deadline) {
      try {
        const reconnected = await this.client.reconnect(room.reconnectionToken);
        this.attach(reconnected);
        return;
      } catch {
        await sleep(delay);
        delay = Math.min(delay * 2, 4000);
      }
    }
    store.emit("connection", { state: "closed", code });
  }

  async leave(): Promise<void> {
    this.leaving = true;
    await this.room?.leave(true);
  }

  send(type: string, payload?: unknown): void {
    this.room?.send(type, payload as never);
  }

  requestLeaderboard(): void {
    this.send("requestLeaderboard");
  }
}

/** Shared connection instance used across the UI and the game scene. */
export const net = new NetClient();
